import { ImoduleFilter, WAREHOUSE_FILTER_ACTIVE_QUERY_PATH, WAREHOUSE_FILTER_QUERY_NAME } from 'types/global';
import { FilterProcessor, INVOICE_FILTER_VALUE } from 'types/orders';

const createInFilter = (fieldPath: string, values: (string | number)[]): string | null => {
  if (!values?.length) return null;
  const encodedValues = values.map(value => encodeURIComponent(value)).join(',');
  return `filters[${fieldPath}][$in]=${encodedValues}`;
};

const filterProcessors: Record<string, FilterProcessor> = {
  countryDestination: (filter: ImoduleFilter) => {
    return createInFilter('country.name', filter.values || []);
  },
  paymentType: (filter: ImoduleFilter) => {
    return createInFilter('payment.name', filter.values || []);
  },
  company: (filter: ImoduleFilter) => {
    if (!filter.values?.length) return null;
    return createInFilter('order.organizationId', filter.values || []);
  },

  invoice: (filter: ImoduleFilter) => {
    if (!filter.values?.length) return null;
    const selectedValue = String(filter.values[0]);

    const invoiceFilterMap: Record<string, string> = {
      [INVOICE_FILTER_VALUE.LOADED]: `filters[invoice.invoiceUrl][$ne]=null`,
      [INVOICE_FILTER_VALUE.PENDING]: `filters[invoice.invoiceUrl][$eq]=null`
    };

    return invoiceFilterMap[selectedValue] || null;
  },

  month: (filter: ImoduleFilter) => {
    if (!filter.values?.length) return null;
    const ranges = (filter.values || []).map(v => String(v).split(','));
    const validRanges = ranges.filter(([start, end]) => start && end);
    if (!validRanges.length) return null;
    const firstDay = validRanges.reduce((min, [start]) => (start < min ? start : min), validRanges[0][0]);
    const lastDay = validRanges.reduce((max, [, end]) => (end > max ? end : max), validRanges[0][1]);
    return `filters[shipment.createdAt][$between]=${firstDay},${lastDay}`;
  },

  [WAREHOUSE_FILTER_QUERY_NAME]: (filter: ImoduleFilter) => {
    if (!filter.values?.map(String).includes('true')) return null;
    return `filters[${WAREHOUSE_FILTER_ACTIVE_QUERY_PATH}][$eq]=true`;
  }
};

/**
 * Procesa un filtro individual usando su procesador configurado o formato por defecto.
 * @param filter - Filtro a procesar
 * @returns String con el filtro procesado o null si no tiene valores
 */
const processFilter = (filter: ImoduleFilter): string | null => {
  if (!filter.values?.length) return null;

  const processor = filterProcessors[filter.name];
  if (processor) {
    return processor(filter);
  }

  const encodedValues = filter.values.map(value => encodeURIComponent(value)).join(',');
  const includesOperator = filter.includes === 'in' ? '$in' : filter.includes;
  return `filters[${filter.name}][${includesOperator}]=${encodedValues}`;
};

/**
 * Convierte el parámetro sort (JSON) en los query params sortBy y sortDirection.
 * @param sortParam - Valor del parámetro sort (JSON codificado en URI)
 * @returns Array de strings ['sortBy=...', 'sortDirection=...'] o [] si no hay sort
 */
const processSortParam = (sortParam: string | null): string[] => {
  if (!sortParam) return [];
  const sortData = JSON.parse(decodeURIComponent(sortParam));
  const propertyToSort = sortData?.propertyToSort || 'orderId';
  const sortDirectionValue = sortData?.value || 'desc';
  const sortDirection = sortDirectionValue.toUpperCase();
  return [
    `sortBy=${encodeURIComponent(propertyToSort)}`,
    `sortDirection=${encodeURIComponent(sortDirection)}`
  ];
};

/**
 * Convierte los query params procesando los filtros y convirtiendo el filtro de país al formato correcto.
 * @param additionalQueryParams - Query params adicionales como string
 * @returns Query params procesados como string
 */
export const processQueryParams = (additionalQueryParams?: string): string => {
  if (!additionalQueryParams) return '';

  try {
    const urlParams = new URLSearchParams(additionalQueryParams.replace('?', ''));
    const filtersParam = urlParams.get('filters');
    const sortParam = urlParams.get('sort');
    const otherParams: string[] = [];

    urlParams.forEach((value, key) => {
      if (key !== 'filters' && key !== 'sort') {
        otherParams.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    const processedSortParams = processSortParam(sortParam);

    if (!filtersParam) {
      const allParams = [...processedSortParams, ...otherParams];
      return allParams?.length > 0 ? `&${allParams.join('&')}` : '';
    }

    const filters: ImoduleFilter[] = JSON.parse(decodeURIComponent(filtersParam));
    const processedFilters: string[] = [];

    filters.forEach(filter => {
      const processedFilter = processFilter(filter);
      if (processedFilter) {
        processedFilters.push(processedFilter);
      }
    });

    const allParams = [...processedFilters, ...processedSortParams, ...otherParams];
    return allParams?.length > 0 ? `&${allParams.join('&')}` : '';
  } catch (error) {
    return additionalQueryParams.replace('?', '&');
  }
};
